import { inject, Injectable } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  calculateLoginLockRemaining,
  createLoginAccountKey,
  formatLoginLockClock,
  ILoginLockInfo,
  ILoginLockState
} from '@shared';

const LOGIN_LOCK_STORAGE_PREFIX = 'login-lock:';
const LOGIN_LOCK_TIMER_INTERVAL = 1000;

@Injectable({
  providedIn: 'root'
})
export class LoginLockService {
  private readonly stateSubject = new BehaviorSubject<ILoginLockState | null>(null);
  private readonly modalSrv = inject(NzModalService);
  private timerId: number | null = null;

  readonly state$: Observable<ILoginLockState | null> = this.stateSubject.asObservable();

  /**
   * 恢复指定账号在本地保存的锁定状态。
   *
   * @param account 登录账号
   * @returns 当前有效锁定状态；未锁定返回 null
   * @example restore('demo')
   */
  restore(account: string): ILoginLockState | null {
    const accountKey = createLoginAccountKey(account);
    const currentState = this.stateSubject.value;
    if (currentState?.accountKey === accountKey) {
      return this.refreshState(currentState);
    }

    const lockUntil = this.readLockUntil(accountKey);
    if (lockUntil === null) {
      this.publishState(null);
      return null;
    }

    const nextState = this.createState(accountKey, lockUntil);
    this.publishState(nextState);
    return nextState;
  }

  /**
   * 保存后端返回的账号锁定状态并启动倒计时。
   *
   * @param account 登录账号
   * @param lockInfo 后端返回的剩余时长或截止时间
   * @returns 已保存的锁定状态
   * @example startLock('demo', { remainingSeconds: 900 })
   */
  startLock(account: string, lockInfo: ILoginLockInfo): ILoginLockState {
    const now = Date.now();
    const accountKey = createLoginAccountKey(account);
    const lockUntil =
      lockInfo.lockUntil !== undefined && lockInfo.lockUntil > now
        ? lockInfo.lockUntil
        : now + Math.max(1, lockInfo.remainingSeconds) * 1000;
    const nextState = this.createState(accountKey, lockUntil);
    this.writeLockUntil(accountKey, lockUntil);
    this.publishState(nextState);
    return nextState;
  }

  /**
   * 清除指定账号的锁定状态。
   *
   * @param account 登录账号
   * @returns 无返回值
   * @example clear('demo')
   */
  clear(account: string): void {
    const accountKey = createLoginAccountKey(account);
    this.removeLockUntil(accountKey);
    if (this.stateSubject.value?.accountKey === accountKey) {
      this.publishState(null);
    }
  }

  /**
   * 展示后端锁定结果对应的登录失败弹框。
   *
   * @param remainingSeconds 弹框展示时的剩余秒数
   * @param onConfirm 用户点击确定后的回调
   * @returns 无返回值
   * @example showLockedModal(899, () => form.patchValue({ password: null }))
   */
  showLockedModal(remainingSeconds: number, onConfirm: () => void): void {
    this.modalSrv.warning({
      nzTitle: '登录失败',
      nzContent: `密码连续输入错误 5 次，账号已被锁定 15 分钟，请 ${formatLoginLockClock(remainingSeconds)} 后重试`,
      nzOkText: '确定',
      nzCancelText: null,
      nzClosable: false,
      nzMaskClosable: false,
      nzKeyboard: false,
      nzOnOk: onConfirm
    });
  }

  /**
   * 刷新锁定状态中的剩余秒数。
   *
   * @param state 当前锁定状态
   * @returns 更新后的状态；已过期返回 null
   * @example refreshState({ accountKey: 'x', lockUntil: Date.now() + 1000, remainingSeconds: 1 })
   */
  private refreshState(state: ILoginLockState): ILoginLockState | null {
    const remainingSeconds = calculateLoginLockRemaining(state.lockUntil);
    if (remainingSeconds <= 0) {
      this.removeLockUntil(state.accountKey);
      this.publishState(null);
      return null;
    }

    const nextState = { ...state, remainingSeconds };
    this.publishState(nextState);
    return nextState;
  }

  /**
   * 根据截止时间构造锁定状态。
   *
   * @param accountKey 账号摘要
   * @param lockUntil 锁定截止时间
   * @returns 含当前剩余秒数的状态
   * @example createState('key', Date.now() + 900_000)
   */
  private createState(accountKey: string, lockUntil: number): ILoginLockState {
    return {
      accountKey,
      lockUntil,
      remainingSeconds: calculateLoginLockRemaining(lockUntil)
    };
  }

  /**
   * 发布状态并管理倒计时定时器。
   *
   * @param state 下一状态
   * @returns 无返回值
   * @example publishState(null)
   */
  private publishState(state: ILoginLockState | null): void {
    this.stateSubject.next(state);
    this.stopTimer();
    if (state) {
      this.startTimer();
    }
  }

  private startTimer(): void {
    this.timerId = window.setInterval(() => {
      const state = this.stateSubject.value;
      if (!state) {
        this.stopTimer();
        return;
      }

      const remainingSeconds = calculateLoginLockRemaining(state.lockUntil);
      if (remainingSeconds <= 0) {
        this.removeLockUntil(state.accountKey);
        this.publishState(null);
        return;
      }

      this.stateSubject.next({ ...state, remainingSeconds });
    }, LOGIN_LOCK_TIMER_INTERVAL);
  }

  private stopTimer(): void {
    if (this.timerId === null) return;
    window.clearInterval(this.timerId);
    this.timerId = null;
  }

  private readLockUntil(accountKey: string): number | null {
    const storage = this.getStorage();
    if (!storage) return null;

    try {
      const value = Number(storage.getItem(`${LOGIN_LOCK_STORAGE_PREFIX}${accountKey}`));
      if (Number.isFinite(value) && value > Date.now()) return value;
      this.removeLockUntil(accountKey);
    } catch {
      return null;
    }
    return null;
  }

  private writeLockUntil(accountKey: string, lockUntil: number): void {
    const storage = this.getStorage();
    if (!storage) return;

    try {
      storage.setItem(`${LOGIN_LOCK_STORAGE_PREFIX}${accountKey}`, String(lockUntil));
    } catch {
      // 浏览器禁用本地存储时仅保留当前页面内存状态。
    }
  }

  private removeLockUntil(accountKey: string): void {
    const storage = this.getStorage();
    if (!storage) return;

    try {
      storage.removeItem(`${LOGIN_LOCK_STORAGE_PREFIX}${accountKey}`);
    } catch {
      // 浏览器禁用本地存储时不阻断登录流程。
    }
  }

  private getStorage(): Storage | null {
    try {
      return typeof window === 'undefined' ? null : window.localStorage;
    } catch {
      return null;
    }
  }
}
